/**
 * Footer Component - Site footer with links and info
 * Provides navigation links, contact info, and legal information
 */

export class Footer {
  render(container: HTMLElement): void {
    container.innerHTML = `
      <footer class="footer" role="contentinfo">
        <div class="footer-content">
          <div class="footer-section">
            <h3 class="footer-title">🇸🇱 Sierra Leone Mpox Tracker</h3>
            <p class="footer-description">
              Real-time outbreak information to keep Sierra Leone safe and informed.
              Built for citizens, by the Ministry of Health.
            </p>
            <div class="footer-emergency">
              <strong>Emergency Hotline: <a href="tel:117">117</a></strong>
            </div>
          </div>
          
          <div class="footer-section">
            <h4 class="footer-heading">Quick Links</h4>
            <nav class="footer-nav">
              <a href="#symptoms" class="footer-link">🏥 Symptoms</a>
              <a href="#vaccines" class="footer-link">💉 Vaccines</a>
              <a href="#prevention" class="footer-link">🛡️ Prevention</a>
              <a href="#districts" class="footer-link">🗺️ Districts</a>
            </nav>
          </div>
          
          <div class="footer-section">
            <h4 class="footer-heading">Resources</h4>
            <nav class="footer-nav">
              <a href="https://www.who.int/emergencies/disease-outbreak-news/item/2022-DON385" 
                 class="footer-link" target="_blank" rel="noopener">
                WHO Information
              </a>
              <a href="https://www.cdc.gov/poxvirus/mpox/" 
                 class="footer-link" target="_blank" rel="noopener">
                CDC Guidelines
              </a>
              <a href="tel:+23222224236" class="footer-link">📞 Ministry of Health</a>
              <a href="mailto:info@health.gov.sl" class="footer-link">✉️ Contact Us</a>
            </nav>
          </div>
          
          <div class="footer-section">
            <h4 class="footer-heading">Data & Technology</h4>
            <div class="footer-tech">
              <p class="data-update">
                Data updated every 30 minutes from health facilities across Sierra Leone
              </p>
              <div class="tech-stack">
                <span class="tech-badge">📱 Mobile-First</span>
                <span class="tech-badge">⚡ Fast Loading</span>
                <span class="tech-badge">🔄 Offline Ready</span>
                <span class="tech-badge">♿ Accessible</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="footer-bottom">
          <div class="footer-bottom-content">
            <div class="footer-legal">
              <p>
                © 2025 Republic of Sierra Leone - Ministry of Health and Sanitation
              </p>
              <div class="footer-links">
                <a href="#privacy" class="footer-link-small">Privacy Policy</a>
                <a href="#terms" class="footer-link-small">Terms of Use</a>
                <a href="#accessibility" class="footer-link-small">Accessibility</a>
                <a href="#open-source" class="footer-link-small">Open Source</a>
              </div>
            </div>
            
            <div class="footer-social">
              <span class="social-label">Follow Updates:</span>
              <div class="social-links">
                <a href="#" class="social-link" aria-label="Twitter">🐦</a>
                <a href="#" class="social-link" aria-label="Facebook">📘</a>
                <a href="#" class="social-link" aria-label="WhatsApp">💬</a>
              </div>
            </div>
          </div>
          
          <div class="footer-disclaimer">
            <p>
              <strong>Disclaimer:</strong> This information is for educational purposes. 
              For medical emergencies, call 117 immediately. Always consult healthcare 
              professionals for medical advice.
            </p>
          </div>
        </div>
      </footer>
    `;

    this.setupEventListeners();
    this.addStyles();
  }

  private setupEventListeners(): void {
    // Handle internal navigation
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        event.preventDefault();
        const section = target.getAttribute('href')?.substring(1);
        this.handleInternalNavigation(section || '');
      }
    });
  }

  private handleInternalNavigation(section: string): void {
    switch (section) {
      case 'symptoms':
        this.showSymptomsInfo();
        break;
      case 'vaccines':
        this.showVaccineInfo();
        break;
      case 'prevention':
        this.showPreventionInfo();
        break;
      case 'districts':
        this.scrollToDistricts();
        break;
      case 'privacy':
        this.showPrivacyPolicy();
        break;
      case 'terms':
        this.showTermsOfUse();
        break;
      case 'accessibility':
        this.showAccessibilityInfo();
        break;
      case 'open-source':
        this.showOpenSourceInfo();
        break;
    }
  }

  private showSymptomsInfo(): void {
    // Dispatch event to show symptoms modal
    const event = new CustomEvent('modal:show', {
      detail: { type: 'symptoms' }
    });
    document.dispatchEvent(event);
  }

  private showVaccineInfo(): void {
    // Dispatch event to show vaccine modal
    const event = new CustomEvent('modal:show', {
      detail: { type: 'vaccines' }
    });
    document.dispatchEvent(event);
  }

  private showPreventionInfo(): void {
    const modal = this.createModal(
      '🛡️ Prevention Guidelines',
      `
        <div class="prevention-guide">
          <h3>How to Protect Yourself and Others</h3>
          
          <div class="prevention-section">
            <h4>🧼 Personal Hygiene</h4>
            <ul>
              <li>Wash hands frequently with soap and water for at least 20 seconds</li>
              <li>Use alcohol-based hand sanitizer when soap is not available</li>
              <li>Avoid touching your face, eyes, nose, and mouth</li>
              <li>Cover coughs and sneezes with your elbow or tissue</li>
            </ul>
          </div>
          
          <div class="prevention-section">
            <h4>🏠 At Home</h4>
            <ul>
              <li>Clean and disinfect frequently touched surfaces</li>
              <li>Wash clothes and bedding in hot water</li>
              <li>Isolate if you have symptoms</li>
              <li>Keep good ventilation in living spaces</li>
            </ul>
          </div>
          
          <div class="prevention-section">
            <h4>👥 Social Measures</h4>
            <ul>
              <li>Avoid close contact with people who are sick</li>
              <li>Stay home if you feel unwell</li>
              <li>Avoid sharing personal items</li>
              <li>Practice physical distancing when possible</li>
            </ul>
          </div>
          
          <div class="prevention-section">
            <h4>💉 Vaccination</h4>
            <ul>
              <li>Get vaccinated if you are eligible</li>
              <li>Follow vaccination schedule recommendations</li>
              <li>Continue protective measures even after vaccination</li>
            </ul>
          </div>
          
          <div class="alert alert-info">
            <strong>Remember:</strong> Prevention is your best protection. 
            These simple steps can significantly reduce your risk of infection.
          </div>
        </div>
      `
    );
    this.showModal(modal);
  }

  private scrollToDistricts(): void {
    // Scroll to district selector
    const districtSection = document.querySelector('#district-selector');
    if (districtSection) {
      districtSection.scrollIntoView({ behavior: 'smooth' });
    }
  }

  private showPrivacyPolicy(): void {
    const modal = this.createModal(
      '🔒 Privacy Policy',
      `
        <div class="legal-document">
          <h3>Your Privacy Matters</h3>
          <p>
            The Sierra Leone Mpox Tracker is committed to protecting your privacy 
            and ensuring transparent use of data.
          </p>
          
          <h4>Data Collection</h4>
          <ul>
            <li>We do not collect personal information</li>
            <li>No registration or login required</li>
            <li>Usage analytics are anonymized</li>
            <li>Location data is optional and not stored</li>
          </ul>
          
          <h4>Data Storage</h4>
          <ul>
            <li>Health statistics are public information</li>
            <li>Data is cached locally for offline access</li>
            <li>No personal health information is stored</li>
            <li>You can clear local data anytime</li>
          </ul>
          
          <h4>Data Sharing</h4>
          <ul>
            <li>Health data comes from official government sources</li>
            <li>No data is shared with third parties</li>
            <li>All data follows WHO privacy guidelines</li>
          </ul>
          
          <h4>Your Rights</h4>
          <ul>
            <li>Right to access public health information</li>
            <li>Right to delete local app data</li>
            <li>Right to contact us with questions</li>
          </ul>
          
          <p>
            <strong>Contact:</strong> For privacy questions, email 
            <a href="mailto:privacy@health.gov.sl">privacy@health.gov.sl</a>
          </p>
        </div>
      `
    );
    this.showModal(modal);
  }

  private showTermsOfUse(): void {
    const modal = this.createModal(
      '📋 Terms of Use',
      `
        <div class="legal-document">
          <h3>Terms of Service</h3>
          <p>
            By using the Sierra Leone Mpox Tracker, you agree to these terms.
          </p>
          
          <h4>Intended Use</h4>
          <ul>
            <li>For informational and educational purposes only</li>
            <li>Not a substitute for professional medical advice</li>
            <li>Emergency situations require immediate medical attention</li>
            <li>Always consult healthcare professionals for health decisions</li>
          </ul>
          
          <h4>Data Accuracy</h4>
          <ul>
            <li>Data is updated regularly from official sources</li>
            <li>We strive for accuracy but cannot guarantee completeness</li>
            <li>Information may be delayed or revised</li>
            <li>Official health authorities are the ultimate source</li>
          </ul>
          
          <h4>Acceptable Use</h4>
          <ul>
            <li>Use responsibly and ethically</li>
            <li>Do not attempt to disrupt the service</li>
            <li>Respect intellectual property rights</li>
            <li>Report issues or errors to help improve the service</li>
          </ul>
          
          <h4>Limitations</h4>
          <ul>
            <li>Service provided "as is" without warranties</li>
            <li>Government reserves right to modify or discontinue</li>
            <li>Not liable for decisions based on this information</li>
          </ul>
          
          <div class="alert alert-warning">
            <strong>Medical Emergency:</strong> For immediate medical help, 
            call 117 or visit your nearest health facility.
          </div>
        </div>
      `
    );
    this.showModal(modal);
  }

  private showAccessibilityInfo(): void {
    const modal = this.createModal(
      '♿ Accessibility Statement',
      `
        <div class="accessibility-info">
          <h3>Accessibility Commitment</h3>
          <p>
            We are committed to ensuring digital accessibility for people with disabilities.
          </p>
          
          <h4>Standards Compliance</h4>
          <ul>
            <li>WCAG 2.1 AA compliance</li>
            <li>Keyboard navigation support</li>
            <li>Screen reader compatibility</li>
            <li>High contrast mode support</li>
          </ul>
          
          <h4>Accessibility Features</h4>
          <ul>
            <li>📱 Works on all devices and screen sizes</li>
            <li>⌨️ Full keyboard navigation</li>
            <li>🔊 Screen reader announcements</li>
            <li>🎨 High contrast color schemes</li>
            <li>📖 Clear, simple language</li>
            <li>⚡ Fast loading on slow connections</li>
          </ul>
          
          <h4>Alternative Access</h4>
          <ul>
            <li>📞 Phone: Call 117 for emergency information</li>
            <li>💬 WhatsApp: Send message to [number]</li>
            <li>📧 Email: info@health.gov.sl</li>
            <li>🏥 In person: Visit any health facility</li>
          </ul>
          
          <h4>Feedback</h4>
          <p>
            Help us improve accessibility. Report issues or suggestions to 
            <a href="mailto:accessibility@health.gov.sl">accessibility@health.gov.sl</a>
          </p>
          
          <h4>Assistive Technology Tested</h4>
          <ul>
            <li>NVDA screen reader</li>
            <li>JAWS screen reader</li>
            <li>VoiceOver (iOS/macOS)</li>
            <li>TalkBack (Android)</li>
          </ul>
        </div>
      `
    );
    this.showModal(modal);
  }

  private showOpenSourceInfo(): void {
    const modal = this.createModal(
      '🔓 Open Source',
      `
        <div class="open-source-info">
          <h3>Open Source Commitment</h3>
          <p>
            This tracker is built with transparency and community collaboration in mind.
          </p>
          
          <h4>Open Source Benefits</h4>
          <ul>
            <li>🔍 Transparent and auditable code</li>
            <li>🤝 Community contributions welcome</li>
            <li>🛡️ Enhanced security through peer review</li>
            <li>🌍 Reusable for other health emergencies</li>
          </ul>
          
          <h4>Technologies Used</h4>
          <ul>
            <li>⚡ Vite - Fast build tool</li>
            <li>🗺️ Leaflet - Interactive maps</li>
            <li>📊 Chart.js - Data visualization</li>
            <li>☁️ Cloudflare Workers - Edge computing</li>
            <li>💾 Cloudflare KV - Data storage</li>
          </ul>
          
          <h4>Contributing</h4>
          <p>
            Developers, designers, and health professionals can contribute to 
            improving this system for Sierra Leone and other countries.
          </p>
          
          <h4>License</h4>
          <p>
            Released under MIT License - free to use, modify, and distribute 
            for public health purposes.
          </p>
          
          <div class="contact-info">
            <p>
              <strong>Technical Contact:</strong><br>
              Email: <a href="mailto:tech@health.gov.sl">tech@health.gov.sl</a><br>
              GitHub: [Repository link when available]
            </p>
          </div>
        </div>
      `
    );
    this.showModal(modal);
  }

  private createModal(title: string, content: string): HTMLElement {
    const modal = document.createElement('div');
    modal.className = 'modal-overlay';
    modal.innerHTML = `
      <div class="modal-content">
        <div class="modal-header">
          <h2>${title}</h2>
          <button class="modal-close" aria-label="Close modal">×</button>
        </div>
        <div class="modal-body">
          ${content}
        </div>
        <div class="modal-footer">
          <button class="btn btn-primary modal-close">Close</button>
        </div>
      </div>
    `;
    return modal;
  }

  private showModal(modal: HTMLElement): void {
    document.body.appendChild(modal);

    // Setup close handlers
    const closeButtons = modal.querySelectorAll('.modal-close');
    const closeModal = () => {
      modal.remove();
      document.body.style.overflow = '';
    };

    closeButtons.forEach(btn => {
      btn.addEventListener('click', closeModal);
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Focus management
    const firstButton = modal.querySelector('button');
    firstButton?.focus();
  }

  private addStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      .footer-content {
        display: grid;
        grid-template-columns: 1fr;
        gap: var(--spacing-8);
        margin-bottom: var(--spacing-8);
      }
      
      @media (min-width: 768px) {
        .footer-content {
          grid-template-columns: repeat(2, 1fr);
        }
      }
      
      @media (min-width: 1024px) {
        .footer-content {
          grid-template-columns: repeat(4, 1fr);
        }
      }
      
      .footer-section {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
      }
      
      .footer-title {
        font-size: var(--font-size-lg);
        font-weight: 700;
        color: white;
        margin-bottom: var(--spacing-2);
      }
      
      .footer-heading {
        font-size: var(--font-size-base);
        font-weight: 600;
        color: white;
        margin-bottom: var(--spacing-3);
      }
      
      .footer-description {
        color: var(--gray-300);
        line-height: 1.6;
        margin-bottom: var(--spacing-3);
      }
      
      .footer-emergency {
        background: var(--danger-color);
        padding: var(--spacing-3);
        border-radius: var(--radius);
        text-align: center;
      }
      
      .footer-emergency a {
        color: white;
        text-decoration: none;
        font-size: var(--font-size-lg);
      }
      
      .footer-nav {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-2);
      }
      
      .footer-link {
        color: var(--gray-300);
        text-decoration: none;
        font-size: var(--font-size-sm);
        transition: var(--transition);
      }
      
      .footer-link:hover {
        color: white;
        text-decoration: underline;
      }
      
      .footer-tech {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
      }
      
      .data-update {
        color: var(--gray-300);
        font-size: var(--font-size-sm);
        line-height: 1.5;
      }
      
      .tech-stack {
        display: flex;
        flex-wrap: wrap;
        gap: var(--spacing-2);
      }
      
      .tech-badge {
        background: var(--primary-light);
        color: white;
        padding: var(--spacing-1) var(--spacing-2);
        border-radius: var(--radius-sm);
        font-size: var(--font-size-xs);
        font-weight: 500;
      }
      
      .footer-bottom {
        border-top: 1px solid var(--gray-600);
        padding-top: var(--spacing-6);
      }
      
      .footer-bottom-content {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-4);
        margin-bottom: var(--spacing-4);
      }
      
      @media (min-width: 768px) {
        .footer-bottom-content {
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        }
      }
      
      .footer-legal p {
        color: var(--gray-400);
        font-size: var(--font-size-sm);
        margin-bottom: var(--spacing-2);
      }
      
      .footer-links {
        display: flex;
        flex-wrap: wrap;
        gap: var(--spacing-4);
      }
      
      .footer-link-small {
        color: var(--gray-400);
        text-decoration: none;
        font-size: var(--font-size-xs);
        transition: var(--transition);
      }
      
      .footer-link-small:hover {
        color: var(--gray-200);
        text-decoration: underline;
      }
      
      .footer-social {
        display: flex;
        align-items: center;
        gap: var(--spacing-3);
      }
      
      .social-label {
        color: var(--gray-400);
        font-size: var(--font-size-sm);
      }
      
      .social-links {
        display: flex;
        gap: var(--spacing-2);
      }
      
      .social-link {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        background: var(--gray-700);
        border-radius: var(--radius);
        text-decoration: none;
        transition: var(--transition);
      }
      
      .social-link:hover {
        background: var(--primary-color);
        transform: translateY(-1px);
      }
      
      .footer-disclaimer {
        background: rgba(255, 255, 255, 0.1);
        padding: var(--spacing-4);
        border-radius: var(--radius);
        margin-top: var(--spacing-4);
      }
      
      .footer-disclaimer p {
        color: var(--gray-300);
        font-size: var(--font-size-xs);
        text-align: center;
        line-height: 1.5;
        margin: 0;
      }
      
      /* Modal content styles */
      .prevention-guide,
      .legal-document,
      .accessibility-info,
      .open-source-info {
        max-height: 70vh;
        overflow-y: auto;
      }
      
      .prevention-section {
        margin-bottom: var(--spacing-6);
      }
      
      .prevention-section h4 {
        color: var(--primary-color);
        margin-bottom: var(--spacing-3);
      }
      
      .prevention-section ul {
        margin-left: var(--spacing-4);
      }
      
      .prevention-section li {
        margin-bottom: var(--spacing-1);
        line-height: 1.5;
      }
    `;
    
    document.head.appendChild(style);
  }
}